import { useModal } from '@ebay/nice-modal-react';
import { useEffect, useRef } from 'react';

import { clearLoginUnlockResetCompleted } from 'contexts/MatrixContext/loginSecretStorageUnlock';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { CrossSigningStatusEnum } from 'contexts/MatrixDeviceContext/types';
import useMatrixDeviceContext from 'contexts/MatrixDeviceContext/useMatrixDeviceContext';
import useEnforceSecurityCode from 'hooks/useEnforceSecurityCode';
import { SessionStateKeys, useSessionState } from 'hooks/useSessionState';
import useMxCookies from 'modules/Authentication/hooks/useMxCookies';
import CreateSecurityCodeModal from 'modules/EndToEndEncryption/SecurityCode/CreateSecurityCodeModal/CreateSecurityCodeModal';

/**
 * Shows the CreateSecurityCodeModal once after a fresh cross-signing bootstrap (typically
 * a brand-new account on first login). `useStartMatrixClient` cannot show the modal
 * directly because the auth-router transition triggered by `setMxLoggedIn(true)` tears
 * down anything mounted in the same turn — that was the ~50ms flash bug.
 *
 * Instead, `useStartMatrixClient` (fresh bootstrap) and `ResetKeysModal` (post-reset)
 * write a session flag and this gate (mounted at the app root, alongside
 * `EnforceSecurityCodeGate`) shows the modal via `useEffect` after the post-login
 * render has committed. The flag is consumed once and removed.
 *
 * The `hasShownRef` ref prevents the modal from re-opening when the user dismisses it:
 * after `modal.remove()` flips `modal.visible` back to `false`, this effect re-runs, and
 * without the ref guard it would re-trigger `modal.show(...)` because `useSessionState`
 * does not always reliably propagate the cleared value within the same render cycle.
 *
 * For enforced users, `EnforceSecurityCodeGate` may also fire; the `modal.visible` guard
 * still prevents a double-show. When enforcement is on we pass `enforced: true` so the
 * modal cannot be skipped — matching the original `useBootstrapCrossSigning` behavior.
 */
const BootstrapSecurityCodeGate = () => {
  const [needsPrompt, , clearPrompt] = useSessionState<boolean>(
    SessionStateKeys.NEEDS_SECURITY_CODE_PROMPT_AFTER_BOOTSTRAP,
    false
  );
  const isEnforced = useEnforceSecurityCode();
  const { mxLoggedIn } = useMxCookies();
  const { hasSecurityCode, crossSigningStatus, refreshHasSecurityCode } = useMatrixDeviceContext();
  const { matrixClient } = useMatrixContext({ requireDefinedMatrixClient: false });
  const modal = useModal(CreateSecurityCodeModal);
  const hasShownRef = useRef(false);
  const prevNeedsPromptRef = useRef<boolean | null>(null);
  const wasVisibleRef = useRef(false);

  // After a reset the identity can flip to NOT_VERIFIED for a window while the SDK settles the new
  // keys (BUG-021, BUG-016). `needsPrompt` is the authoritative signal that a create-code prompt was
  // deliberately scheduled (fresh bootstrap, login-unlock reset, or post-login reset), so once
  // crypto has resolved at least once (status !== UNKNOWN) we open the modal without waiting for
  // VERIFIED — the strict VERIFIED gate stranded post-login resets where the status never settled to
  // VERIFIED before the user gave up (BUG-025, reproduced consistently on localhost). UNKNOWN is
  // still excluded so we never prompt mid crypto-init on a fresh bootstrap.
  const crossSigningReadyForPrompt = crossSigningStatus !== CrossSigningStatusEnum.UNKNOWN;

  const isReady =
    needsPrompt === true &&
    mxLoggedIn === true &&
    !!matrixClient &&
    crossSigningReadyForPrompt &&
    hasSecurityCode === false;

  // Allow a new prompt cycle when the flag is set again (e.g. after reset keys).
  useEffect(() => {
    if (needsPrompt === true && prevNeedsPromptRef.current !== true) {
      hasShownRef.current = false;
    }
    prevNeedsPromptRef.current = needsPrompt;
  }, [needsPrompt]);

  useEffect(() => {
    if (!isReady || modal.visible || hasShownRef.current) return;
    hasShownRef.current = true;
    modal.show({
      mode: 'create',
      showSetupDescription: true,
      onSuccess: refreshHasSecurityCode,
      ...(isEnforced && { enforced: true }),
    });
  }, [isReady, modal, isEnforced, refreshHasSecurityCode]);

  // Release the post-reset suppression signals only after the create-code modal has fully closed
  // (success or dismiss), never at open. BUG-025 lets this gate open while crossSigningStatus is a
  // transient NOT_VERIFIED; clearing the signals at open dropped the picker / reset-section guards
  // during that settling window, so both modals flashed for a frame (BUG-027). Keeping the prompt
  // flag set while the modal is open is safe — `modal.visible` + `hasShownRef` prevent a re-show.
  useEffect(() => {
    if (modal.visible) {
      wasVisibleRef.current = true;
      return;
    }
    if (!wasVisibleRef.current) return;
    wasVisibleRef.current = false;
    clearLoginUnlockResetCompleted();
    clearPrompt();
  }, [modal.visible, clearPrompt]);

  return null;
};

export default BootstrapSecurityCodeGate;
